import { useCallback, useMemo } from 'react'

import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { Chart } from '../components/Chart.js'
import type { ChartProps } from '../types/index.js'
import { AnalyticsFeeder } from '../utils/analyticsFeeder.js'
import type { AnalyticsQuery } from '../utils/generateAnalyticsQuery.js'
import {
  generateAnalyticsQuery,
  generateTimeFrame,
  generateGroupBy,
} from '../utils/generateAnalyticsQuery.js'
import { DefaultChartColors } from '../utils/index.js'
import { getValueFromAnalyticsData } from '../utils/parser.js'

export const NewMembersArea = ({
  scope,
  id,
  timeFrame,
  className,
  segment,
}: ChartProps) => {
  const { $t } = useI18n()

  const createQueries = useCallback(() => {
    const generalQuery = {
      timeFrame: generateTimeFrame({ timeFrame }),
      where: [
        { field: scope, value: id },
        ...(scope === 'network' ? [{ field: 'space', value: null }] : []),
        ...(segment?.where ? [segment.where] : []),
      ],
      limit: timeFrame.xAxisCount + 2,
    }

    const currentQuery: AnalyticsQuery = {
      ...generalQuery,
      selects: [{ field: 'count(join)', as: 'Members' }],
    }

    const prevQuery: AnalyticsQuery = {
      ...currentQuery,
      timeFrame: generateTimeFrame({ timeFrame, target: 'previous' }),
    }

    const chartQuery: AnalyticsQuery = {
      ...currentQuery,
      groupBy: {
        ...generateGroupBy(timeFrame.granularity),
      },
      order: { by: 'timeBucket', dir: 'asc' },
    }

    if (timeFrame.id === 'allTime') {
      return [
        generateAnalyticsQuery(chartQuery),
        generateAnalyticsQuery(currentQuery),
      ]
    }

    return [
      generateAnalyticsQuery(chartQuery),
      generateAnalyticsQuery(currentQuery),
      generateAnalyticsQuery(prevQuery),
    ]
  }, [id, scope, timeFrame, segment])

  const queries = useMemo(() => [createQueries()], [createQueries])

  return (
    <AnalyticsFeeder queries={queries[0]}>
      {({ data, isLoading }) => (
        <Chart
          type="Area"
          title={$t({
            defaultMessage: 'Net new {MEMBERS}',
            id: 'Analytics.NewMembersChart.Title',
          })}
          description={timeFrame.text}
          current={getValueFromAnalyticsData(data[1], 'Members')}
          previous={getValueFromAnalyticsData(data[2], 'Members')}
          toolTipText={$t({
            defaultMessage:
              'Change in number of {MEMBERS} (new {MEMBERS} - deleted {MEMBERS})',
            id: 'Analytics.NewMembersChart.Tooltip',
          })}
          data={data[0]}
          timeFrame={timeFrame}
          indexBy={['Members']}
          labels={[
            $t({
              id: 'Generics.Members',
              defaultMessage: '{MEMBERS_CC}',
            }),
          ]}
          syncId="analytics"
          isLoading={isLoading}
          className={className}
          ColorTheme={DefaultChartColors}
        />
      )}
    </AnalyticsFeeder>
  )
}
